/**
 * Boxes: blocks placed on a slideshow slide like on a magazine page.
 *
 * A stored spread is a bare [left, right] array, and many readers rely on
 * that, so a slide's boxes live on its lead entry: the left side of a spread,
 * or the single image. A bare id turns into an object once it has boxes.
 *
 * Positions and sizes are percent of the whole sheet (both pages of a spread),
 * so a box can cross the gutter. Boxes always draw above the pictures; among
 * themselves, later boxes draw on top.
 */

import { BLOCK_TYPES, createBlock } from '../blocks/schema.js';

export const BOX_BLOCK_TYPES = Object.freeze([
    BLOCK_TYPES.RICHTEXT,
    BLOCK_TYPES.COLUMNS,
    BLOCK_TYPES.VECTOR,
    BLOCK_TYPES.EMBED,
]);

/** What "Add a block" offers on a slide. */
export const BOX_PALETTE = Object.freeze([
    { type: BLOCK_TYPES.RICHTEXT, label: 'Text', hint: 'Words, headings and links' },
    { type: BLOCK_TYPES.COLUMNS, label: 'Columns', hint: 'Texts side by side' },
    { type: BLOCK_TYPES.VECTOR, label: 'Vector', hint: 'Brand SVG, tinted or original colours' },
    { type: BLOCK_TYPES.EMBED, label: 'Embed', hint: 'A YouTube or Vimeo link' },
]);

export const BOX_LIMITS = Object.freeze({
    count: 12,
    min: -50,
    max: 150,
    minSize: 3,
    maxSize: 200,
    maxPad: 120,
});

/** How a box shows on a phone: its own reading slide, drawn on its page, or not at all. */
export const BOX_PHONE = Object.freeze(['slide', 'page', 'hidden']);
export const BOX_VALIGN = Object.freeze(['top', 'center', 'bottom']);
/** Screens a box can be hidden on, see screenOf. */
export const BOX_SCREENS = Object.freeze(['desktop', 'tall', 'tablet', 'phone']);

/** Width of the sheet box contents are laid out at, then scaled to the real sheet. */
export const BOX_REFERENCE_WIDTH = 1200;

/** The pages a slide breaks into on a phone: its picture pages, then the page of text after them. */
export const PHONE_PAGES = Object.freeze(['page1', 'page2', 'text']);
/** Where a narrower block sits across the phone's text page. */
export const PHONE_TEXT_ALIGN = Object.freeze(['start', 'center', 'end']);
/** Width of a block on the phone's text page, percent of the reading column. */
export const PHONE_TEXT_WIDTH = Object.freeze({ min: 30, max: 100 });

const PAPER_PREFIX = 'paper-';
const BOX_ID = /^[A-Za-z0-9_-]{1,64}$/;
const COLOUR = /^(#[0-9a-fA-F]{3,8}|rgba?\([0-9.,\s%]+\)|transparent)$/;

let counter = 0;
const stamp = () => {
    counter += 1;
    return `${Date.now().toString(36)}-${counter}`;
};

/** Pictures move and scale; these keep their shape, so only the width is set. */
export const keepsAspect = (type) => type === BLOCK_TYPES.VECTOR || type === BLOCK_TYPES.EMBED;

export const phoneDefault = (type) => (keepsAspect(type) ? 'page' : 'slide');

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const round = (value) => Math.round(value * 100) / 100;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const finite = (value) => typeof value === 'number' && Number.isFinite(value);

/** A blank page: a slide, or one side of a spread, with no picture. */
export function isPaper(entry) {
    return isObject(entry) && entry.paper === true && typeof entry.id === 'string' && entry.id.startsWith(PAPER_PREFIX);
}

export function createPaper() {
    return { id: `${PAPER_PREFIX}${Date.now().toString(36)}${(counter += 1).toString(36)}`, paper: true };
}

/** A spread of two blank pages, for text alone. */
export function createBlankSpread() {
    return [createPaper(), createPaper()];
}

/** The id an entry (bare id, object, or one side of a spread) stands for. */
export function entryId(entry) {
    if (typeof entry === 'string') return entry;
    return isObject(entry) && typeof entry.id === 'string' ? entry.id : null;
}

/**
 * A new box holding a new block of `type`, `w` percent of the sheet wide and
 * centred on (cx, cy).
 */
export function createBox(type, { cx = 50, cy = 50, w = 40 } = {}) {
    let block = createBlock(type);
    if (type === BLOCK_TYPES.RICHTEXT) block = { ...block, maxWidth: null };
    if (type === BLOCK_TYPES.VECTOR) block = { ...block, width: { value: 100, unit: '%' } };
    const width = clamp(w, BOX_LIMITS.minSize, BOX_LIMITS.maxSize);
    return {
        id: `box-${stamp()}`,
        x: round(clamp(cx - width / 2, BOX_LIMITS.min, BOX_LIMITS.max)),
        y: round(clamp(cy - (keepsAspect(type) ? width / 3 : 5), BOX_LIMITS.min, BOX_LIMITS.max)),
        w: round(width),
        h: null,
        phone: phoneDefault(type),
        block,
    };
}

/**
 * The list with a new box of `type`, 40% of a page wide and centred on page
 * `side` (the whole sheet for a single picture). Unchanged when full.
 */
export function addBox(boxes, type, { single = true, pageWidth = 50, side = 0 } = {}) {
    const list = Array.isArray(boxes) ? boxes : [];
    if (list.length >= BOX_LIMITS.count || !BOX_BLOCK_TYPES.includes(type)) return list;
    const page = single ? 100 : pageWidth;
    const cx = !single && side === 1 ? 100 - page / 2 : page / 2;
    return [...list, createBox(type, { cx, cy: 50, w: page * 0.4 })];
}

/** The page of a slide new blocks go on: a blank page beside a picture, else the first. */
export function boxSide(entry) {
    return Array.isArray(entry) && !isPaper(entry[0]) && isPaper(entry[1]) ? 1 : 0;
}

/** The boxes on a slide's lead entry (or on a spread, through its left side). */
export function boxesOf(entry) {
    if (Array.isArray(entry)) return boxesOf(entry[0]);
    return isObject(entry) && Array.isArray(entry.boxes) ? entry.boxes : [];
}

/** The lead entry with `key` set to `value` (or dropped for null); a side left with only its id goes back to a bare id. */
function withLead(entry, key, value) {
    if (Array.isArray(entry)) return [withLead(entry[0], key, value), entry[1]];
    if (typeof entry === 'string') return value ? { id: entry, [key]: value } : entry;
    if (!isObject(entry)) return entry;
    const { [key]: _old, ...rest } = entry;
    if (value) return { ...rest, [key]: value };
    return Object.keys(rest).length === 1 && typeof rest.id === 'string' ? rest.id : rest;
}

/** The entry with these boxes; a side left with only its id goes back to a bare id. */
export function withBoxes(entry, boxes) {
    const list = Array.isArray(boxes) ? boxes : [];
    return withLead(entry, 'boxes', list.length ? list : null);
}

/** A slide's phone pages made safe: their order and where the text page's blocks sit; null when all default. */
export function cleanPhonePages(raw) {
    if (!isObject(raw)) return null;
    const out = {};
    if (Array.isArray(raw.order)) {
        const order = raw.order.filter((page, index, list) => PHONE_PAGES.includes(page) && list.indexOf(page) === index);
        if (order.length) out.order = order;
    }
    if (Array.isArray(raw.hidden)) {
        const hidden = PHONE_PAGES.filter((page) => raw.hidden.includes(page));
        if (hidden.length) out.hidden = hidden;
    }
    if (BOX_VALIGN.includes(raw.valign) && raw.valign !== 'center') out.valign = raw.valign;
    if (Array.isArray(raw.textOrder)) {
        const textOrder = raw.textOrder.filter((id, index, list) => typeof id === 'string' && id && list.indexOf(id) === index);
        if (textOrder.length) out.textOrder = textOrder.slice(0, 200);
    }
    return Object.keys(out).length ? out : null;
}

/** How a slide (or a spread, through its left side) is split on a phone; null when the default. */
export function phonePagesOf(entry) {
    if (Array.isArray(entry)) return phonePagesOf(entry[0]);
    return isObject(entry) ? cleanPhonePages(entry.phonePages) : null;
}

export function withPhonePages(entry, phonePages) {
    return withLead(entry, 'phonePages', cleanPhonePages(phonePages));
}

/** What a slide's lead entry carries for its blocks: the boxes, and how a phone splits the slide. */
export function slideKept(entry) {
    const boxes = boxesOf(entry);
    const phonePages = phonePagesOf(entry);
    return {
        ...(boxes.length > 0 && { boxes }),
        ...(phonePages && { phonePages }),
    };
}

/** Server check for a lead entry's phonePages. */
export function validatePhonePages(raw) {
    if (raw === undefined) return { valid: true };
    if (!isObject(raw)) return { valid: false, error: 'Phone pages must be an object' };
    if (raw.order !== undefined && !(Array.isArray(raw.order)
        && raw.order.every((page, index) => PHONE_PAGES.includes(page) && raw.order.indexOf(page) === index))) {
        return { valid: false, error: `Phone page order can only list each of: ${PHONE_PAGES.join(', ')}` };
    }
    if (raw.hidden !== undefined && !(Array.isArray(raw.hidden) && raw.hidden.every((page) => PHONE_PAGES.includes(page)))) {
        return { valid: false, error: `Hidden phone pages can only be: ${PHONE_PAGES.join(', ')}` };
    }
    if (raw.valign !== undefined && !BOX_VALIGN.includes(raw.valign)) {
        return { valid: false, error: 'Phone pages have an invalid vertical position' };
    }
    if (raw.textOrder !== undefined && !(Array.isArray(raw.textOrder) && raw.textOrder.length <= 200
        && raw.textOrder.every((id) => typeof id === 'string' && id.length > 0 && id.length <= 100))) {
        return { valid: false, error: 'Phone text order must be a list of block ids' };
    }
    return { valid: true };
}

function cleanNumber(value, min, max, fallback) {
    return finite(value) ? round(clamp(value, min, max)) : fallback;
}

/** One stored box made safe to render, or null when it can't be. */
export function normalizeBox(raw) {
    if (!isObject(raw) || !isObject(raw.block)) return null;
    const { block } = raw;
    if (!BOX_BLOCK_TYPES.includes(block.type) || typeof block.id !== 'string' || !block.id) return null;
    const id = typeof raw.id === 'string' && BOX_ID.test(raw.id) ? raw.id : `box-${block.id}`.slice(0, 64);
    const box = {
        id,
        x: cleanNumber(raw.x, BOX_LIMITS.min, BOX_LIMITS.max, 10),
        y: cleanNumber(raw.y, BOX_LIMITS.min, BOX_LIMITS.max, 10),
        w: cleanNumber(raw.w, BOX_LIMITS.minSize, BOX_LIMITS.maxSize, 40),
        h: keepsAspect(block.type) ? null : cleanNumber(raw.h, BOX_LIMITS.minSize, BOX_LIMITS.maxSize, null),
        phone: BOX_PHONE.includes(raw.phone) ? raw.phone : phoneDefault(block.type),
        block,
    };
    if (typeof raw.bg === 'string' && COLOUR.test(raw.bg.trim())) box.bg = raw.bg.trim();
    if (finite(raw.pad) && raw.pad > 0) box.pad = Math.round(clamp(raw.pad, 0, BOX_LIMITS.maxPad));
    if (box.h !== null && BOX_VALIGN.includes(raw.valign) && raw.valign !== 'top') box.valign = raw.valign;
    const hideOn = cleanHideOn(raw.hideOn);
    if (hideOn.length) box.hideOn = hideOn;
    const phoneAt = cleanPhoneAt(raw.phoneAt, block.type);
    if (phoneAt) box.phoneAt = phoneAt;
    const phoneText = cleanPhoneText(raw.phoneText);
    if (phoneText) box.phoneText = phoneText;
    if (isObject(raw.phoneBlock) && raw.phoneBlock.type === block.type) box.phoneBlock = { ...raw.phoneBlock, id: block.id };
    return box;
}

/** Where a box sits on its picture page on a phone, in that page's percent; null to follow the wide layout. */
function cleanPhoneAt(raw, type) {
    if (!isObject(raw) || !finite(raw.x) || !finite(raw.y) || !finite(raw.w)) return null;
    const at = {
        x: cleanNumber(raw.x, BOX_LIMITS.min, BOX_LIMITS.max, 0),
        y: cleanNumber(raw.y, BOX_LIMITS.min, BOX_LIMITS.max, 0),
        w: cleanNumber(raw.w, BOX_LIMITS.minSize, BOX_LIMITS.maxSize, 40),
        h: keepsAspect(type) ? null : cleanNumber(raw.h, BOX_LIMITS.minSize, BOX_LIMITS.maxSize, null),
    };
    if (raw.page === 0 || raw.page === 1) at.page = raw.page;
    return at;
}

/** A box's width and place across the phone's text page; null for the full column. */
function cleanPhoneText(raw) {
    if (!isObject(raw)) return null;
    const out = {};
    if (finite(raw.w) && raw.w < PHONE_TEXT_WIDTH.max) out.w = Math.round(clamp(raw.w, PHONE_TEXT_WIDTH.min, PHONE_TEXT_WIDTH.max));
    if (out.w && PHONE_TEXT_ALIGN.includes(raw.align) && raw.align !== 'start') out.align = raw.align;
    return Object.keys(out).length ? out : null;
}

function validatePhoneAt(box) {
    const at = box.phoneAt;
    if (at === undefined || at === null) return null;
    if (!isObject(at)) return `Box ${box.id} phone place must be an object`;
    if (at.page !== undefined && at.page !== 0 && at.page !== 1) return `Box ${box.id} phone page must be 0 or 1`;
    for (const key of ['x', 'y']) {
        if (!finite(at[key]) || at[key] < BOX_LIMITS.min || at[key] > BOX_LIMITS.max) {
            return `Box ${box.id} phone ${key} must be a number from ${BOX_LIMITS.min} to ${BOX_LIMITS.max}`;
        }
    }
    for (const key of ['w', 'h']) {
        if (key === 'h' && (at.h === null || at.h === undefined)) continue;
        if (!finite(at[key]) || at[key] < BOX_LIMITS.minSize || at[key] > BOX_LIMITS.maxSize) {
            return `Box ${box.id} phone size must be from ${BOX_LIMITS.minSize} to ${BOX_LIMITS.maxSize}`;
        }
    }
    return null;
}

function validatePhoneText(box) {
    const text = box.phoneText;
    if (text === undefined || text === null) return null;
    if (!isObject(text)) return `Box ${box.id} text page settings must be an object`;
    if (text.w !== undefined && !(finite(text.w) && text.w >= PHONE_TEXT_WIDTH.min && text.w <= PHONE_TEXT_WIDTH.max)) {
        return `Box ${box.id} text page width must be from ${PHONE_TEXT_WIDTH.min} to ${PHONE_TEXT_WIDTH.max}`;
    }
    if (text.align !== undefined && !PHONE_TEXT_ALIGN.includes(text.align)) {
        return `Box ${box.id} text page place must be one of: ${PHONE_TEXT_ALIGN.join(', ')}`;
    }
    return null;
}

function cleanHideOn(list) {
    return Array.isArray(list) ? BOX_SCREENS.filter((screen) => list.includes(screen)) : [];
}

/**
 * The screen a slideshow visitor has, from resolveExperience: a wide desktop
 * window, a tall or narrow one with a mouse, a tablet, or a phone.
 */
export function screenOf(experience) {
    if (!experience) return 'desktop';
    if (experience.phone) return 'phone';
    if (experience.touch) return 'tablet';
    return experience.shape === 'big-wide' ? 'desktop' : 'tall';
}

/** Whether a box shows on `screen`; the older phone: 'hidden' hides it on phones and tablets. */
export function boxShownOn(box, screen) {
    if (!box || !screen) return true;
    if (box.phone === 'hidden' && (screen === 'phone' || screen === 'tablet')) return false;
    return !(Array.isArray(box.hideOn) && box.hideOn.includes(screen));
}

/** A slide's boxes, cleaned: unusable ones dropped, ids unique, at most BOX_LIMITS.count. */
export function normalizeBoxes(list) {
    if (!Array.isArray(list)) return [];
    const seen = new Set();
    const out = [];
    list.forEach((raw) => {
        const box = normalizeBox(raw);
        if (!box || seen.has(box.id) || out.length >= BOX_LIMITS.count) return;
        seen.add(box.id);
        out.push(box);
    });
    return out;
}

/** Server check for a lead entry's boxes. */
export function validateBoxes(list) {
    if (list === undefined) return { valid: true };
    if (!Array.isArray(list)) return { valid: false, error: 'Boxes must be a list' };
    if (list.length > BOX_LIMITS.count) {
        return { valid: false, error: `A slide can hold at most ${BOX_LIMITS.count} blocks` };
    }
    const ids = new Set();
    for (const box of list) {
        if (!isObject(box)) return { valid: false, error: 'Each box must be an object' };
        if (typeof box.id !== 'string' || !BOX_ID.test(box.id)) return { valid: false, error: 'Each box needs an id' };
        if (ids.has(box.id)) return { valid: false, error: `Box ${box.id} is listed twice` };
        ids.add(box.id);
        if (!isObject(box.block) || typeof box.block.id !== 'string' || !box.block.id) {
            return { valid: false, error: `Box ${box.id} has no block` };
        }
        if (!BOX_BLOCK_TYPES.includes(box.block.type)) {
            return { valid: false, error: `Box ${box.id} can't hold a ${box.block.type} block` };
        }
        for (const key of ['x', 'y']) {
            if (!finite(box[key]) || box[key] < BOX_LIMITS.min || box[key] > BOX_LIMITS.max) {
                return { valid: false, error: `Box ${box.id} ${key} must be a number from ${BOX_LIMITS.min} to ${BOX_LIMITS.max}` };
            }
        }
        if (!finite(box.w) || box.w < BOX_LIMITS.minSize || box.w > BOX_LIMITS.maxSize) {
            return { valid: false, error: `Box ${box.id} width must be from ${BOX_LIMITS.minSize} to ${BOX_LIMITS.maxSize}` };
        }
        if (box.h !== null && box.h !== undefined
            && (!finite(box.h) || box.h < BOX_LIMITS.minSize || box.h > BOX_LIMITS.maxSize)) {
            return { valid: false, error: `Box ${box.id} height must be from ${BOX_LIMITS.minSize} to ${BOX_LIMITS.maxSize}` };
        }
        if (box.phone !== undefined && !BOX_PHONE.includes(box.phone)) {
            return { valid: false, error: `Box ${box.id} phone must be one of: ${BOX_PHONE.join(', ')}` };
        }
        if (box.bg !== undefined && !(typeof box.bg === 'string' && COLOUR.test(box.bg.trim()))) {
            return { valid: false, error: `Box ${box.id} has an invalid background` };
        }
        if (box.pad !== undefined && !(finite(box.pad) && box.pad >= 0 && box.pad <= BOX_LIMITS.maxPad)) {
            return { valid: false, error: `Box ${box.id} spacing must be from 0 to ${BOX_LIMITS.maxPad}` };
        }
        if (box.valign !== undefined && !BOX_VALIGN.includes(box.valign)) {
            return { valid: false, error: `Box ${box.id} has an invalid vertical position` };
        }
        if (box.hideOn !== undefined && !(Array.isArray(box.hideOn) && box.hideOn.every((screen) => BOX_SCREENS.includes(screen)))) {
            return { valid: false, error: `Box ${box.id} can only be hidden on: ${BOX_SCREENS.join(', ')}` };
        }
        if (box.phoneBlock !== undefined && box.phoneBlock !== null
            && !(isObject(box.phoneBlock) && box.phoneBlock.type === box.block.type)) {
            return { valid: false, error: `Box ${box.id} phone text must be the same kind of block` };
        }
        const phoneError = validatePhoneAt(box) || validatePhoneText(box);
        if (phoneError) return { valid: false, error: phoneError };
    }
    return { valid: true };
}

function findInBlock(block, id) {
    if (!block) return null;
    if (block.id === id) return block;
    if (block.type === BLOCK_TYPES.COLUMNS && Array.isArray(block.cells)) {
        for (const cell of block.cells) {
            const found = findInBlock(cell, id);
            if (found) return found;
        }
    }
    return null;
}

/** The box holding block `blockId` (the box's block or a columns cell), and where its slide is. */
export function findBox(images, blockId) {
    if (!Array.isArray(images) || !blockId) return null;
    for (let index = 0; index < images.length; index += 1) {
        const boxes = boxesOf(images[index]);
        for (const box of boxes) {
            const block = findInBlock(box.block, blockId);
            if (block) return { index, box, block };
        }
    }
    return null;
}

export function findBoxBlock(images, blockId) {
    return findBox(images, blockId)?.block || null;
}

/** The slide (index into images) a box sits on, by box id. */
export function findBoxById(images, boxId) {
    if (!Array.isArray(images) || !boxId) return null;
    for (let index = 0; index < images.length; index += 1) {
        const box = boxesOf(images[index]).find((entry) => entry.id === boxId);
        if (box) return { index, box };
    }
    return null;
}

/**
 * A slide's boxes as a phone shows them, one page at a time: `pages[i]` holds
 * the boxes kept on page i (in that page's own percent), `reading` the ones
 * that move to a reading slide after it. Hidden boxes are left out.
 * A box placed for phones (phoneAt) keeps that page and place; any other
 * belongs to the page that holds its centre.
 */
export function phoneBoxes(boxes, { spread = false, screen = 'phone' } = {}) {
    const list = normalizeBoxes(boxes);
    const pages = spread ? [[], []] : [[]];
    const reading = [];
    list.forEach((stored) => {
        if (stored.phone === 'hidden' || !boxShownOn(stored, screen)) return;
        const { phoneBlock, ...rest } = stored;
        const box = phoneBlock ? { ...rest, block: phoneBlock } : rest;
        if (box.phone === 'slide') {
            reading.push(box);
            return;
        }
        const side = phoneSideOf(box, spread);
        pages[side].push({ ...box, ...phoneGeometry(box, spread) });
    });
    // Reading order: top to bottom, then left to right.
    reading.sort((a, b) => (a.y - b.y) || (a.x - b.x));
    return { pages, reading };
}

/**
 * The phone text page's blocks in the order set for them; blocks not in
 * `order` (added since) keep their top-to-bottom place after the listed ones.
 */
export function inTextOrder(boxes, order) {
    if (!Array.isArray(order) || !order.length) return boxes;
    const rank = (box) => {
        const at = order.indexOf(box.id);
        return at === -1 ? order.length : at;
    };
    return boxes.map((box, index) => ({ box, index }))
        .sort((a, b) => (rank(a.box) - rank(b.box)) || (a.index - b.index))
        .map(({ box }) => box);
}

/** The picture page (0 or 1) a box kept on its page shows on. */
export function phoneSideOf(box, spread = true) {
    if (!spread) return 0;
    const page = box.phoneAt?.page;
    if (page === 0 || page === 1) return page;
    return box.x + box.w / 2 < 50 ? 0 : 1;
}

/** A page box's place and size on its phone page, in that page's percent. */
export function phoneGeometry(box, spread = true) {
    if (box.phoneAt) {
        const { x, y, w, h } = box.phoneAt;
        return { x, y, w, h: h ?? null };
    }
    if (!spread) return { x: box.x, y: box.y, w: box.w, h: box.h ?? null };
    const side = phoneSideOf(box, spread);
    return { x: round((box.x - side * 50) * 2), y: box.y, w: round(box.w * 2), h: box.h ?? null };
}

/** Where a box shows on a phone: 'text', a picture page (0 or 1), or 'hidden'. */
export function phonePlaceOf(box, spread = true) {
    if (!box || box.phone === 'hidden') return 'hidden';
    if (box.phone === 'slide') return 'text';
    return phoneSideOf(box, spread);
}

/** Where a box shows on a phone, counting one hidden on phones alone. */
export function phoneShownPlace(box, spread = true) {
    return boxShownOn(box, 'phone') ? phonePlaceOf(box, spread) : 'hidden';
}

/** The box hidden on phones only; tablets keep it. */
export function hideOnPhone(box) {
    const hideOn = cleanHideOn([...(box.hideOn || []), 'phone']);
    return { ...box, hideOn };
}

/**
 * The box moved to `place` on a phone ('text', 0, 1 or 'hidden'). Put on a
 * picture page, it keeps the size it had there and stays on the page.
 */
export function placeOnPhone(source, place, spread = true) {
    if (place === 'hidden') return hideOnPhone(source);
    const { hideOn: _hidden, ...unhidden } = source;
    const hideOn = cleanHideOn(source.hideOn).filter((screen) => screen !== 'phone');
    const box = hideOn.length ? { ...unhidden, hideOn } : unhidden;
    if (place === 'text') return { ...box, phone: 'slide' };
    if (!spread) return { ...box, phone: 'page' };
    const { phoneAt: _at, ...rest } = box;
    const side = place === 1 ? 1 : 0;
    const page = { ...rest, phone: 'page' };
    if (phoneSideOf(page, spread) === side && !box.phoneAt) return page;
    const geometry = box.phoneAt && phoneSideOf(box, spread) === side ? box.phoneAt : phoneGeometry(rest, spread);
    const w = Math.min(geometry.w, 100);
    return {
        ...page,
        phoneAt: {
            page: side,
            x: round(clamp(geometry.x, 0, 100 - w)),
            y: round(clamp(geometry.y, 0, 95)),
            w: round(w),
            h: keepsAspect(box.block?.type) ? null : (geometry.h ?? null),
        },
    };
}

/**
 * The box dropped on another phone page. On a picture page it is centred on
 * `point` ({ x, y } in that page's percent) when one is given.
 */
export function dropOnPhonePage(source, place, point = null, spread = true) {
    const placed = placeOnPhone(source, place, spread);
    if (place === 'text' || place === 'hidden' || !point) return placed;
    const geometry = phoneGeometry(placed, spread);
    const w = Math.min(geometry.w, 100);
    const h = geometry.h ?? 0;
    return {
        ...placed,
        phoneAt: {
            ...(spread && { page: place === 1 ? 1 : 0 }),
            x: round(clamp(point.x - w / 2, 0, 100 - w)),
            y: round(clamp(point.y - h / 2, 0, 95)),
            w: round(w),
            h: geometry.h ?? null,
        },
    };
}

/**
 * A slide's boxes after one of its phone pages was edited: `shown` are the
 * boxes that page drew (from phoneSplit), `edited` what came back. A moved or
 * resized box is placed for phones from then on; one taken off the page is
 * hidden on phones; typed text becomes the box's own text for phones.
 */
export function withPhonePageEdit(boxes, shown, edited, { side = 0, spread = true } = {}) {
    const before = new Map(shown.map((box) => [box.id, box]));
    const after = new Map(edited.map((box) => [box.id, box]));
    let changed = false;
    const next = (Array.isArray(boxes) ? boxes : []).map((box) => {
        const was = before.get(box.id);
        if (!was) return box;
        const now = after.get(box.id);
        if (!now) {
            changed = true;
            return hideOnPhone(box);
        }
        const moved = ['x', 'y', 'w', 'h'].some((key) => (now[key] ?? null) !== (was[key] ?? null));
        if (!moved && now.block === was.block) return box;
        changed = true;
        return {
            ...(now.block === was.block ? box : withPhoneBlock(box, now.block)),
            ...(moved && {
                phoneAt: {
                    ...(spread && { page: side }),
                    x: now.x,
                    y: now.y,
                    w: now.w,
                    h: keepsAspect(box.block?.type) ? null : (now.h ?? null),
                },
            }),
        };
    });
    return changed ? next : boxes;
}

/** The box with its own block for phones; the same block as wide screens drops the copy. */
export function withPhoneBlock(box, block) {
    const { phoneBlock: _own, ...rest } = box;
    if (!block || block === box.block || JSON.stringify(block) === JSON.stringify(box.block)) return rest;
    return { ...rest, phoneBlock: { ...block, id: box.block.id } };
}

/** The block a phone shows for a box: its own copy, or the wide-screen one. */
export function phoneBlockOf(box) {
    return box?.phoneBlock || box?.block || null;
}

/** Whether a slide has anything set for phones only: a box placed for them, or its pages' order or text position. */
export function hasPhoneLayout(entry) {
    return Boolean(phonePagesOf(entry)) || boxesOf(entry).some((box) => box.phoneAt || box.phoneText);
}

/** The slide's boxes with every phone-only place and size dropped, back to following the wide layout. */
export function withoutPhoneLayout(boxes) {
    return (Array.isArray(boxes) ? boxes : []).map((box) => {
        if (!box.phoneAt && !box.phoneText) return box;
        const { phoneAt: _at, phoneText: _text, ...rest } = box;
        return rest;
    });
}

/** `parts` in the slide's chosen order: the ones it names first, the rest after in their own order. */
export function orderPhonePages(parts, order) {
    if (!Array.isArray(order) || !order.length) return parts;
    const rank = (part) => {
        const at = order.indexOf(part.page);
        return at < 0 ? order.length + parts.indexOf(part) : at;
    };
    return [...parts].sort((a, b) => rank(a) - rank(b));
}

/**
 * The pages one slide breaks into on a phone, in the order they show:
 * `{ page: 'page1' | 'page2', side, boxes }` for each picture page and
 * `{ page: 'text', boxes, valign }` for the blocks read after them.
 * `sides` describes each side: `{ paper, video, missing }`. A blank page
 * shows only when blocks stay on it; a spread of blank pages with nothing on
 * either side is its text page alone. Pages turned off for phones are left
 * out, or with `withHidden` kept and marked `hidden` (for the editor).
 */
export function phoneSplit(entry, sides, { screen = 'phone', withHidden = false } = {}) {
    const spread = sides.length === 2;
    const { pages, reading } = phoneBoxes(boxesOf(entry), { spread, screen });
    const phonePages = phonePagesOf(entry);
    const parts = [];
    sides.forEach((side, index) => {
        if (side?.missing) return;
        const boxes = side?.video ? [] : pages[index];
        if (side?.paper && !boxes.length) return;
        parts.push({ page: `page${index + 1}`, side: index, boxes });
    });
    const blank = sides.every((side) => side?.paper);
    if ((parts.length || blank) && reading.length) {
        parts.push({ page: 'text', boxes: inTextOrder(reading, phonePages?.textOrder), valign: phonePages?.valign || 'center' });
    }
    const hidden = phonePages?.hidden || [];
    const marked = parts.map((part) => (hidden.includes(part.page) ? { ...part, hidden: true } : part));
    return orderPhonePages(withHidden ? marked : marked.filter((part) => !part.hidden), phonePages?.order);
}

/** The slide (index into images) showing picture `imageId` on either side; -1 when none. */
export function slideIndexOf(images, imageId) {
    if (!Array.isArray(images) || !imageId) return -1;
    return images.findIndex((entry) => (Array.isArray(entry)
        ? entry.some((side) => entryId(side) === imageId)
        : entryId(entry) === imageId));
}

/** images with `fn(boxes)` applied to slide `index`'s boxes; the same array when nothing changes. */
export function updateSlideBoxes(images, index, fn) {
    if (!Array.isArray(images) || !images[index]) return images;
    const before = boxesOf(images[index]);
    const after = fn(before);
    if (after === before) return images;
    const next = [...images];
    next[index] = withBoxes(images[index], after);
    return next;
}

/** images with one box (by box id) replaced by `fn(box)`. */
export function patchBox(images, boxId, fn) {
    const found = findBoxById(images, boxId);
    if (!found) return images;
    return updateSlideBoxes(images, found.index, (boxes) => boxes.map((box) => (box.id === boxId ? fn(box) : box)));
}

function patchInBlock(block, id, fn) {
    if (block.id === id) return fn(block);
    if (block.type === BLOCK_TYPES.COLUMNS && Array.isArray(block.cells)) {
        let changed = false;
        const cells = block.cells.map((cell) => {
            const next = patchInBlock(cell, id, fn);
            if (next !== cell) changed = true;
            return next;
        });
        return changed ? { ...block, cells } : block;
    }
    return block;
}

/** images with block `blockId` (in a box, or a columns cell in one) replaced by `fn(block)`. */
export function patchBoxBlock(images, blockId, fn) {
    const found = findBox(images, blockId);
    if (!found) return images;
    return updateSlideBoxes(images, found.index, (boxes) => {
        let changed = false;
        const next = boxes.map((box) => {
            const block = patchInBlock(box.block, blockId, fn);
            if (block === box.block) return box;
            changed = true;
            return { ...box, block };
        });
        return changed ? next : boxes;
    });
}

/** A spread with its sides swapped: the boxes stay where they are on the sheet, on the new lead. */
export function swapSpreadSides(spread) {
    if (!Array.isArray(spread) || spread.length !== 2) return spread;
    const boxes = boxesOf(spread);
    const phonePages = phonePagesOf(spread);
    return [
        withPhonePages(withBoxes(spread[1], boxes), phonePages),
        withPhonePages(withBoxes(spread[0], []), null),
    ];
}

/** The lead entry of two slides joined into a spread holds both slides' boxes. */
export function mergeBoxes(...entries) {
    const all = entries.flatMap((entry) => boxesOf(entry));
    const seen = new Set();
    return all.filter((box) => {
        if (seen.has(box.id)) return false;
        seen.add(box.id);
        return true;
    }).slice(0, BOX_LIMITS.count);
}

/** Library files one slide's boxes use (vector art and embed posters), so they aren't counted unused. */
export function boxFileIds(entry) {
    const ids = [];
    const visit = (block) => {
        if (!block) return;
        if (block.type === BLOCK_TYPES.VECTOR && typeof block.fileId === 'string' && block.fileId) ids.push(block.fileId);
        if (block.type === BLOCK_TYPES.EMBED && typeof block.posterFileId === 'string' && block.posterFileId) {
            ids.push(block.posterFileId);
        }
        if (block.type === BLOCK_TYPES.COLUMNS && Array.isArray(block.cells)) block.cells.forEach(visit);
    };
    boxesOf(entry).forEach((box) => {
        visit(box.block);
        if (box.phoneBlock) visit(box.phoneBlock);
    });
    return ids;
}

export default {
    BOX_BLOCK_TYPES,
    BOX_PALETTE,
    BOX_LIMITS,
    BOX_PHONE,
    BOX_VALIGN,
    BOX_SCREENS,
    BOX_REFERENCE_WIDTH,
    PHONE_PAGES,
    PHONE_TEXT_ALIGN,
    PHONE_TEXT_WIDTH,
    screenOf,
    boxShownOn,
    createBlankSpread,
    keepsAspect,
    phoneDefault,
    isPaper,
    createPaper,
    entryId,
    createBox,
    addBox,
    boxSide,
    boxesOf,
    withBoxes,
    normalizeBox,
    normalizeBoxes,
    validateBoxes,
    findBox,
    findBoxBlock,
    findBoxById,
    phoneBoxes,
    phoneSideOf,
    phoneGeometry,
    phonePlaceOf,
    placeOnPhone,
    phoneShownPlace,
    hideOnPhone,
    withPhonePageEdit,
    hasPhoneLayout,
    withoutPhoneLayout,
    orderPhonePages,
    phoneSplit,
    cleanPhonePages,
    phonePagesOf,
    withPhonePages,
    slideKept,
    validatePhonePages,
    slideIndexOf,
    updateSlideBoxes,
    patchBox,
    patchBoxBlock,
    swapSpreadSides,
    mergeBoxes,
    boxFileIds,
};
